import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Mail } from "lucide-react";

/**
 * PolicyLayout — shared editorial layout for all GirlyGrove policy pages
 * (Shipping, Refund & Return, Privacy, Terms).  Soft-luxe aesthetic with
 * blush hero, generous serif headings, gold rules between sections.
 */
const PolicyLayout = ({ title, kicker, intro, sections, contactEmail = "support@girlygrove.in" }) => (
  <div data-testid={`policy-${title.toLowerCase().replace(/\s+/g, "-")}`}>
    {/* Breadcrumb */}
    <div className="max-w-[1100px] mx-auto px-5 lg:px-10 pt-8 pb-2 text-[11px] tracking-[0.16em] uppercase text-plum/60 flex items-center gap-2">
      <Link to="/" className="hover:text-blush">Home</Link>
      <ChevronRight size={11} />
      <span className="text-plum/80">{title}</span>
    </div>

    {/* Hero */}
    <section className="pt-8 lg:pt-14 pb-8 lg:pb-12" style={{ background: "#FBEAF0" }}>
      <div className="max-w-[1100px] mx-auto px-5 lg:px-10 text-center">
        <p className="overline mb-4">{kicker}</p>
        <h1
          className="font-serif text-plum leading-[1] tracking-tight"
          style={{ fontSize: "clamp(38px, 5vw, 68px)" }}
        >
          {title}
        </h1>
        {intro && (
          <p className="mt-5 text-plum/70 max-w-[520px] mx-auto text-[15.5px] leading-[1.7]">
            {intro}
          </p>
        )}
      </div>
    </section>

    {/* Body */}
    <section className="py-12 lg:py-20" style={{ background: "#FDF6F0" }}>
      <div className="max-w-[760px] mx-auto px-5 lg:px-10">
        {sections.map((s, idx) => (
          <div key={s.heading} className={idx > 0 ? "mt-10 pt-10 border-t border-gold/20" : ""}>
            <h2 className="font-serif text-plum text-2xl lg:text-3xl mb-4">{s.heading}</h2>
            {s.paragraphs?.map((p, i) => (
              <p key={i} className="text-plum/75 text-[15px] leading-[1.8] mb-3">
                {p}
              </p>
            ))}
            {s.bullets && (
              <ul className="space-y-2 mt-2">
                {s.bullets.map((b, i) => (
                  <li key={i} className="text-plum/75 text-[15px] leading-[1.7] flex gap-3">
                    <span className="text-gold mt-2 flex-shrink-0 w-1 h-1 rounded-full bg-gold" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}

        {/* Contact card */}
        <div className="mt-14 pt-10 border-t border-gold/20">
          <div className="rounded-[20px] bg-cream/70 ring-1 ring-gold/30 p-6 lg:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5 justify-between">
            <div>
              <p className="overline mb-2">Still have a question?</p>
              <p className="font-serif italic text-plum text-xl">We'd love to help — drop us a note.</p>
            </div>
            <a
              href={`mailto:${contactEmail}`}
              className="btn-outline whitespace-nowrap"
              data-testid="policy-contact-email"
            >
              <Mail size={14} /> {contactEmail}
            </a>
          </div>
        </div>
      </div>
    </section>
  </div>
);

export default PolicyLayout;
